import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderCXMLDtoService } from './order-cxml-dto.service';
import {
  PanAppState,
  PlatformService,
  UsfTokenStorageService,
} from '@panamax/app-state';
import {
  Order,
  OrderPunchoutStoreService,
  orderSelectors,
  partnerSelectors,
} from '@usf/ngrx-order';
import { Partner } from '@usf/partner-types';
import { Router } from '@angular/router';
import { UserService } from '@app/user/services';
import { map, take, filter, switchMap, tap } from 'rxjs/operators';
import { combineLatest, iif, of } from 'rxjs';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { Product } from '@shared/models/product.model';
import { PUNCHOUT_TRACING_ACTIONS } from '@shared/tracking/tracing/punchout/punchout-tracing.actions';
import { transmitOrderTrace } from '@order/tracking/tracing/trace-models/transmit-order.trace';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';
import { ORDER_TRACING_ACTIONS } from '@order/tracking/tracing/order-tracing.actions';
import { LoadingSpinnerWMessageModalComponent } from '@shared/components/loading-spinner-w-message-modal/loading-spinner-w-message-modal.component';
import { ModalController } from '@ionic/angular';
import { getLoadingStateOfProducts, LoadingState } from '@usf/ngrx-product';
import { ProductService } from '../../shared/services/product/product.service';

@Injectable({
  providedIn: 'root',
})
export class OrderPunchoutService {
  loadingSpinnerModal: any;
  constructor(
    readonly router: Router,
    private store: Store,
    private tokenStorageService: UsfTokenStorageService,
    private userService: UserService,
    private panAppState: PanAppState,
    private modalController: ModalController,
    private platformService: PlatformService,
    private orderPunchoutStoreService: OrderPunchoutStoreService,
    private productService: ProductService,
  ) {}
  loadingSpinnerModalWMessage: HTMLIonModalElement;

  doPunchoutTransfer(orders: Order[], isRetransfer: boolean = false) {
    const orders$ = iif(
      () => isRetransfer,
      combineLatest(
        orders.map(order =>
          this.store
            .select(orderSelectors.selectChildOrders(order.orderHeader))
            .pipe(
              map((childOrders: Order[]) =>
                childOrders?.length ? childOrders : [order],
              ),
            ),
        ),
      ).pipe(map(arrayOfArrays => [].concat(...arrayOfArrays))),
      of(orders),
    );

    const extractProductNumbers = (orders: Order[]): number[] => {
      const productNumbers = new Set<number>();
      orders.forEach(order => {
        order.orderItems?.forEach(item => {
          if (item.productNumber) {
            productNumbers.add(item.productNumber);
          }
        });
      });
      return Array.from(productNumbers);
    };

    orders$
      .pipe(
        switchMap(punchoutOrders => {
          const requiredProductNumbers = extractProductNumbers(punchoutOrders);
          this.productService.loadProducts(requiredProductNumbers);

          // Check if all required products are in state
          return combineLatest([
            of(punchoutOrders),
            this.store.select(productWithAlternativesSelector),
            of(requiredProductNumbers),
          ]).pipe(
            switchMap(([orders, productMap, requiredProductNumbers]) => {
              const missingProductNumbers = requiredProductNumbers.filter(
                productNumber => !productMap?.has(productNumber),
              );

              if (missingProductNumbers.length > 0) {
                return this.store
                  .select(getLoadingStateOfProducts(missingProductNumbers))
                  .pipe(
                    filter(
                      loadingState =>
                        loadingState === LoadingState.loaded ||
                        loadingState === LoadingState.error,
                    ),
                    take(1),
                    switchMap(() =>
                      combineLatest([
                        of(orders),
                        this.store.select(productWithAlternativesSelector),
                      ]),
                    ),
                  );
              } else {
                return of([orders, productMap] as [
                  Order[],
                  Map<number, Product>,
                ]);
              }
            }),
          );
        }),
        switchMap(
          ([punchoutOrders, productMap]: [Order[], Map<number, Product>]) =>
            combineLatest([
              this.tokenStorageService.getPunchoutSession(),
              of(punchoutOrders),
              this.userService.userCustomizations$(),
              this.panAppState.user$,
              of(productMap),
              this.store.select(partnerSelectors.selectPartner),
            ]),
        ),
        take(1),
      )
      .subscribe(
        ([
          punchoutSession,
          punchoutOrders,
          userCusomizations,
          user,
          productMap,
          partnerState,
        ]) => {
          const buyerCookie = punchoutSession.buyerCookie;
          const browserFormPost = punchoutSession.browserFormPost;
          const userId = user.userId;
          const orderXML = OrderCXMLDtoService.orderToCXMLFormat(
            punchoutOrders,
            productMap,
            partnerState as Partner,
            buyerCookie,
            browserFormPost,
            userId,
            userCusomizations,
          );

          // Build trace object
          const tracking = JSON.parse(JSON.stringify(transmitOrderTrace));
          tracking.tracing.data.isStartOfTrace = true;
          tracking.tracing.data.isEndOfTrace = true;
          tracking.tracing.transformFunc = genericTracingTransformer;
          tracking.tracing.data.attributes.order.punchOutCxml = orderXML;
          if (!!punchoutOrders[0]) {
            const firstOrder = punchoutOrders[0];
            tracking.tracing.data.attributes.order.orderId =
              firstOrder.orderHeader?.orderId;
            tracking.tracing.data.attributes.order.origOrderId =
              firstOrder.orderHeader?.origOrderId;
            tracking.tracing.data.attributes.order.origSplitOrderId =
              firstOrder.orderHeader?.origSplitOrderId;
            tracking.tracing.data.attributes.order.totalAmount =
              firstOrder.orderHeader?.totalPrice;
            tracking.tracing.data.attributes.order.requestedDeliveryDate =
              firstOrder.orderHeader?.requestedDeliveryDate;
            tracking.tracing.data.attributes.order.confirmedDeliveryDate =
              firstOrder.orderHeader?.confirmedDeliveryDate;
            tracking.tracing.data.attributes.order.tandemOrderNumber =
              firstOrder.orderHeader?.tandemOrderNumber;
            tracking.tracing.data.attributes.order.importOrderId =
              firstOrder.orderHeader?.importOrderIdentifier;
            tracking.tracing.data.attributes.order.importedOrdersCount =
              punchoutOrders.length;
          }
          // Dispatch the event for transmit order trace
          this.store.dispatch(
            ORDER_TRACING_ACTIONS.traceTransmitOrderFromClient({ tracking }),
          );

          localStorage.setItem('browserFormPost', browserFormPost);
          localStorage.setItem('orderXML', orderXML);
          this.orderPunchoutStoreService.punchoutLogout(orderXML);
        },
      );
  }

  async createSpinnerWithMessageModal(
    isCartAutotransferFailedTitle: boolean = false,
  ) {
    this.loadingSpinnerModalWMessage = await this.modalController.create({
      component: LoadingSpinnerWMessageModalComponent,
      backdropDismiss: false,
      cssClass:
        this.platformService.platformType !==
        this.platformService.platformEnum.desktop
          ? 'ion-modal-touch'
          : 'modal-desktop-size-lg',
      initialBreakpoint:
        this.platformService.platformType !==
        this.platformService.platformEnum.desktop
          ? 1
          : undefined,
      breakpoints:
        this.platformService.platformType !==
        this.platformService.platformEnum.desktop
          ? [0, 1]
          : undefined,
      componentProps: {
        isCartAutotransferFailed: isCartAutotransferFailedTitle,
      },
    });
    return this.loadingSpinnerModalWMessage?.present();
  }

  async dismissSpinnerModal() {
    return this.loadingSpinnerModalWMessage?.dismiss();
  }

  tracePunchoutTransferClick() {
    this.store.dispatch(PUNCHOUT_TRACING_ACTIONS.transfer());
  }
}
